import { useSyncExternalStore } from "react";

// Notification choices for this browser. They live in localStorage because they describe this
// device, not the account. Reads and writes survive storage being blocked.

export type NotificationPrefs = {
  /** Whether this browser should get notifications. Null until the person decides. */
  enabled: boolean | null;
  /** Put the decrypted message text in local notifications. Off unless turned on. */
  showText: boolean;
  /** The person said "Not now" to the prompt in the chat list. */
  promptDismissed: boolean;
};

const KEYS = {
  enabled: "rhema.notifications",
  showText: "rhema.notifications.text",
  promptDismissed: "rhema.notifications.prompt",
} as const;

const changes = new EventTarget();

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    // Storage is blocked. The choice lasts until the tab closes.
  }
}

let cached: NotificationPrefs | null = null;

export function getPrefs(): NotificationPrefs {
  if (!cached) {
    const enabled = read(KEYS.enabled);
    cached = {
      enabled: enabled === "on" ? true : enabled === "off" ? false : null,
      showText: read(KEYS.showText) === "on",
      promptDismissed: read(KEYS.promptDismissed) === "dismissed",
    };
  }
  return cached;
}

export function setPrefs(patch: Partial<NotificationPrefs>) {
  const next = { ...getPrefs(), ...patch };
  if ("enabled" in patch) write(KEYS.enabled, next.enabled === null ? null : next.enabled ? "on" : "off");
  if ("showText" in patch) write(KEYS.showText, next.showText ? "on" : null);
  if ("promptDismissed" in patch) write(KEYS.promptDismissed, next.promptDismissed ? "dismissed" : null);
  cached = next;
  changes.dispatchEvent(new Event("change"));
}

/** Forgets the cached copy, for tests. */
export function resetPrefs() {
  cached = null;
}

function subscribe(listener: () => void) {
  changes.addEventListener("change", listener);
  return () => changes.removeEventListener("change", listener);
}

export function useNotificationPrefs(): NotificationPrefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}
